import { DEFAULT_WIDGET_ORDER, type WidgetId } from './types';

/** Pure helpers for the widget-stacking order persisted on
 *  `PublishConfig.widgetOrder`. The composer mutates this with up /
 *  down arrows; both the React preview and the server HTML render
 *  iterate the resolved order so the kid sees the same layout in both
 *  places. */

/** Resolve a kid-saved ordering against the canonical orderable
 *  widget list. Drops unknown ids, appends any orderable widgets the
 *  saved list omitted (so a freshly-introduced widget shows up
 *  predictably for already-published projects). */
export function resolveWidgetOrder(
  saved: ReadonlyArray<WidgetId> | undefined,
  canonical: ReadonlyArray<WidgetId> = DEFAULT_WIDGET_ORDER,
): ReadonlyArray<WidgetId> {
  const allowed = new Set(canonical);
  const seen = new Set<WidgetId>();
  const out: WidgetId[] = [];
  for (const id of saved ?? []) {
    if (allowed.has(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  for (const id of canonical) {
    if (!seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  return out;
}

/** Return a new ordering with `id` moved one slot in `direction`.
 *  No-op when at the relevant boundary, or when `id` isn't in the
 *  list. Pure — does not mutate the input. */
export function reorderWidget(
  order: ReadonlyArray<WidgetId>,
  id: WidgetId,
  direction: 'up' | 'down',
): ReadonlyArray<WidgetId> {
  const idx = order.indexOf(id);
  if (idx === -1) return order;
  const swapWith = direction === 'up' ? idx - 1 : idx + 1;
  if (swapWith < 0 || swapWith >= order.length) return order;
  const next = order.slice();
  [next[idx], next[swapWith]] = [next[swapWith]!, next[idx]!];
  return next;
}
